// client/src/lib/offlineAuth.ts
import { indexedDBService } from './indexedDB';
import { cryptoService } from './cryptoService';
import { supabase } from './supabaseClient';

const OFFLINE_EXPIRY_DAYS = 7;
const OFFLINE_EXPIRY_MS = OFFLINE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

interface OfflineAuthResult {
  success: boolean;
  user?: any;
  isOfflineMode?: boolean;
  error?: string;
}

class OfflineAuthService {
  // Store credentials after successful online login
  async storeCredentialsAfterLogin(user: any, password: string, session: any): Promise<void> {
    try {
      console.log('Starting to store offline credentials for user:', user.id);
      
      // Initialize IndexedDB first
      await indexedDBService.init();
      
      const salt = cryptoService.generateSalt();
      const passwordHash = await cryptoService.hashPassword(password, salt);
      
      const credentials = {
        userId: user.id,
        email: user.email,
        passwordHash,
        salt: btoa(String.fromCharCode(...salt)),
        lastLogin: Date.now(),
        expiresAt: Date.now() + OFFLINE_EXPIRY_MS
      };

      const tokens = {
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        expiresAt: session.expires_at * 1000,
        userId: user.id
      };

      console.log('Storing credentials and tokens...');
      await Promise.all([
        indexedDBService.storeCredentials(credentials),
        indexedDBService.storeTokens(tokens)
      ]);

      // Store email lookup immediately
      const userLookup = JSON.parse(localStorage.getItem('agrosense_user_lookup') || '{}');
      userLookup[user.email] = user.id;
      localStorage.setItem('agrosense_user_lookup', JSON.stringify(userLookup));
      console.log('Email lookup stored:', userLookup);

      // Cache user data for offline use
      await this.cacheUserData(user.id);
      
      console.log('Offline credentials stored successfully');
    } catch (error) {
      console.error('Failed to store offline credentials:', error);
      throw error;
    }
  }

  // Attempt offline login
  async attemptOfflineLogin(email: string, password: string): Promise<OfflineAuthResult> {
    try {
      console.log('attemptOfflineLogin called with email:', email);
      
      // Ensure IndexedDB is initialized
      await indexedDBService.init();
      
      // Find user by email (we need to check all stored credentials)
      const credentials = await this.findCredentialsByEmail(email);
      console.log('Found credentials:', credentials ? 'YES' : 'NO');
      
      if (!credentials) {
        return { success: false, error: 'No offline credentials found for this email' };
      }

      // Check if offline period has expired
      console.log('Checking expiry. Now:', Date.now(), 'Expires:', credentials.expiresAt);
      if (Date.now() > credentials.expiresAt) {
        return { success: false, error: 'Offline access expired. Please connect to internet to refresh.' };
      }

      // Verify password
      console.log('Verifying password...');
      const isValidPassword = await cryptoService.verifyPassword(
        password,
        credentials.passwordHash,
        credentials.salt
      );
      console.log('Password valid:', isValidPassword);

      if (!isValidPassword) {
        return { success: false, error: 'Invalid password' };
      }

      // Get cached user data
      console.log('Getting offline data for user:', credentials.userId);
      const offlineData = await indexedDBService.getOfflineData(credentials.userId);
      console.log('Offline data found:', offlineData ? 'YES' : 'NO');
      
      if (!offlineData?.userProfile) {
        return { success: false, error: 'No offline user data available' };
      }

      // Update last login
      await indexedDBService.storeCredentials({
        ...credentials,
        lastLogin: Date.now()
      });

      console.log('Offline login successful, returning user:', offlineData.userProfile);
      return {
        success: true,
        user: offlineData.userProfile,
        isOfflineMode: true
      };

    } catch (error) {
      console.error('Offline login failed:', error);
      return { success: false, error: 'Offline login failed' };
    }
  }

  // Find credentials by email (helper method)
  private async findCredentialsByEmail(email: string): Promise<any> {
    console.log('findCredentialsByEmail - looking for email:', email);
    
    // Since IndexedDB doesn't have email index, we'll store email in localStorage as a lookup
    const userLookup = localStorage.getItem('agrosense_user_lookup');
    console.log('userLookup from localStorage:', userLookup);
    
    if (!userLookup) {
      console.log('No user lookup found in localStorage');
      return null;
    }

    try {
      const lookup = JSON.parse(userLookup);
      console.log('Parsed lookup:', lookup);
      
      const userId = lookup[email];
      console.log('Found userId for email:', userId);
      
      if (!userId) {
        console.log('No userId found for email:', email);
        return null;
      }

      console.log('Getting credentials from IndexedDB for userId:', userId);
      const credentials = await indexedDBService.getCredentials(userId);
      console.log('Retrieved credentials:', credentials ? 'FOUND' : 'NOT FOUND');
      
      return credentials;
    } catch (error) {
      console.error('Error finding credentials by email:', error);
      return null;
    }
  }

  // Cache user data for offline use
  private async cacheUserData(userId: string): Promise<void> {
    try {
      // Fetch and cache user profile
      const { data: profile } = await supabase.auth.getUser();
      
      // Fetch and cache farm data
      const { data: farms } = await supabase
        .from('farms')
        .select('*')
        .eq('user_id', userId);

      // Fetch and cache crop records (last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: cropRecords } = await supabase
        .from('crop_health_reports')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', thirtyDaysAgo);

      // Cache weather history (last 14 days)
      const weatherHistory = this.getCachedWeatherHistory();

      // Cache pest guides (static data)
      const pestGuides = await this.getCachedPestGuides();

      const offlineData = {
        userProfile: profile.user,
        farms: farms || [],
        cropRecords: cropRecords || [],
        weatherHistory,
        pestGuides,
        syncQueue: [],
        lastSync: Date.now()
      };

      await indexedDBService.storeOfflineData(userId, offlineData);

      // Store email lookup
      const userLookup = JSON.parse(localStorage.getItem('agrosense_user_lookup') || '{}');
      userLookup[profile.user?.email] = userId;
      localStorage.setItem('agrosense_user_lookup', JSON.stringify(userLookup));

    } catch (error) {
      console.error('Failed to cache user data:', error);
    }
  }

  // Get cached weather history
  private getCachedWeatherHistory(): any[] {
    try {
      const cached = localStorage.getItem('weather_history');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  }

  // Get cached pest guides
  private async getCachedPestGuides(): Promise<any[]> {
    // Return static pest guide data for offline use
    return [
      {
        id: 'aphids',
        name: 'Aphids',
        symptoms: 'Small green/black insects on leaves, sticky honeydew',
        treatment: 'Neem oil spray, ladybugs, insecticidal soap',
        prevention: 'Regular inspection, companion planting'
      },
      {
        id: 'blight',
        name: 'Late Blight',
        symptoms: 'Dark spots on leaves, white mold underneath',
        treatment: 'Copper fungicide, remove affected plants',
        prevention: 'Good air circulation, avoid overhead watering'
      }
    ];
  }

  // Check if user has valid offline access
  async hasValidOfflineAccess(userId: string): Promise<boolean> {
    try {
      const credentials = await indexedDBService.getCredentials(userId);
      return credentials ? Date.now() < credentials.expiresAt : false;
    } catch {
      return false;
    }
  }

  // Clear offline data (logout)
  async clearOfflineData(userId: string): Promise<void> {
    try {
      await indexedDBService.clearUserData(userId);
      
      // Remove from email lookup
      const userLookup = JSON.parse(localStorage.getItem('agrosense_user_lookup') || '{}');
      Object.keys(userLookup).forEach(email => {
        if (userLookup[email] === userId) {
          delete userLookup[email];
        }
      });
      localStorage.setItem('agrosense_user_lookup', JSON.stringify(userLookup));
    } catch (error) {
      console.error('Failed to clear offline data:', error);
    }
  }

  // Add action to sync queue
  async queueOfflineAction(userId: string, action: any): Promise<void> {
    try {
      const offlineData = await indexedDBService.getOfflineData(userId);
      if (offlineData) {
        const syncQueue = offlineData.syncQueue || [];
        syncQueue.push({
          ...action,
          timestamp: Date.now(),
          id: crypto.randomUUID()
        });
        
        await indexedDBService.storeOfflineData(userId, { syncQueue });
      }
    } catch (error) {
      console.error('Failed to queue offline action:', error);
    }
  }

  // Sync offline actions when back online
  async syncOfflineActions(userId: string): Promise<void> {
    try {
      const offlineData = await indexedDBService.getOfflineData(userId);
      if (!offlineData?.syncQueue?.length) return;

      const syncQueue = [...offlineData.syncQueue];
      
      for (const action of syncQueue) {
        try {
          await this.processOfflineAction(action);
        } catch (error) {
          console.error('Failed to sync action:', action, error);
          // Keep failed actions in queue for retry
          continue;
        }
      }

      // Clear successfully synced actions
      await indexedDBService.storeOfflineData(userId, { 
        syncQueue: [],
        lastSync: Date.now()
      });

    } catch (error) {
      console.error('Failed to sync offline actions:', error);
    }
  }

  // Process individual offline action
  private async processOfflineAction(action: any): Promise<void> {
    switch (action.type) {
      case 'CREATE_CROP_RECORD':
        await supabase.from('crop_health_reports').insert(action.data);
        break;
      case 'UPDATE_FARM':
        await supabase.from('farms').update(action.data).eq('id', action.id);
        break;
      case 'CREATE_TASK':
        await supabase.from('tasks').insert(action.data);
        break;
      default:
        console.warn('Unknown offline action type:', action.type);
    }
  }
}

export const offlineAuthService = new OfflineAuthService();